<template>
  <div>
    <v-dialog v-model="show" persistent :max-width="showAuditLog ? '90vw' : '60vw'">
      <v-wait for="loading defect">
        <template slot="waiting">
          <v-card>
            <v-toolbar dark color="primary"></v-toolbar>
            <vcl-list height="200"></vcl-list>
          </v-card>
        </template>
        <v-card v-if="defect">
          <v-row no-gutters>
            <v-col :cols="showAuditLog ? 6 : 12">
              <v-toolbar dark color="primary" v-if="defect">
                <v-toolbar-title>
                  Defect For
                  <router-link class="accent--text text--lighten-1" :to="{ name: 'unit', params: { unitId: defect.unitId } }">{{ defect.unitNumber }} </router-link>
                </v-toolbar-title>
                <v-subheader class="subtitle"> Submitted By: {{ defect.createdBy }} </v-subheader>
                <v-spacer></v-spacer>
                <v-btn @click="showAuditLog = !showAuditLog" color="accent" v-if="userIsFleetManager">
                  Logs
                  <v-icon small v-if="showAuditLog">chevron_left</v-icon>
                  <v-icon small v-else>chevron_right</v-icon>
                </v-btn>
                <v-btn @click="close" icon text>
                  <v-icon>close</v-icon>
                </v-btn>
              </v-toolbar>
              <v-layout wrap class="mx-4">
                <v-flex xs12 pt-4 v-if="disabled">
                  <v-alert dense border="left" type="warning"> Defect cannot be edited while {{ defect.status | humanizeCase }} </v-alert>
                </v-flex>
                <v-flex xs12 class="pt-4" v-if="!defect.workSessions || !defect.workSessions.some(ws => ws.hivisConfirmationTimestamp)">
                  <v-alert dense border="left" type="error" class="mb-0">
                    Warning: HIVIS has not been completed for this defect
                  </v-alert>
                </v-flex>
                <v-flex xs12 class="pt-3">
                  <v-flex xs12 class="pt-4">
                    <v-chip>{{ defect.ednNumber }}</v-chip>
                  </v-flex>
                  <v-flex class="py-3 caption" xs12> {{ defect.lineNumber ? defect.lineNumber + ': ' : '' }} {{ defect.description }} </v-flex>
                  <v-container>
                    <v-row>
                      <v-col v-if="defect.photoBlobUri" cols="12" class="d-flex align-center justify-center">
                        <v-img :src="defect.photoBlobUri" max-width="300" max-height="240" contain class="rounded elevation-2 md-auto" style="cursor: pointer" @click="openPhotoModal(defect.photoBlobUri, defect.id)" />
                      </v-col>
                    </v-row>
                    <v-row class="my-n3">
                      <v-col cols="3" align-self="center" class="pr-0">
                        <v-label>Completed on</v-label>
                      </v-col>
                      <v-col cols="9">
                        <date v-model="model.completedDate" v-validate="'required'" name="completed date" :error-messages="errors.first('completed date')" :max="today" :min="mindate" :disabled="disabled"> </date>
                      </v-col>
                    </v-row>
                    <v-row class="my-n3">
                      <v-col cols="3" align-self="center" class="pr-0">
                        <v-label>Completed at</v-label>
                      </v-col>
                      <v-col cols="4">
                        <v-select
                          label="Hrs / Kms"
                          :items="intervalTypes"
                          v-model="model.intervalType"
                          v-validate="'required'"
                          :error-messages="errors.first('interval')"
                          name="interval"
                          required
                          @change="reValidate('completed', model.completionInterval)"
                          :disabled="disabled"
                        >
                        </v-select>
                      </v-col>
                      <v-col cols="5">
                        <decimal
                          :disabled="!model.completedDate || disabled"
                          v-model="model.completionInterval"
                          placeholder="e.g. 220"
                          v-validate="`required|completed-in-range-defect-${isHoursIntervalType ? `hrs:${model.completedDate}` : `kms:${model.completedDate}`}`"
                          name="completed"
                          :error-messages="errors.first('completed')"
                          :precision="1"
                          required
                        >
                          <template v-slot:append>{{ model.intervalType }}</template>
                        </decimal>
                      </v-col>
                    </v-row>
                    <v-row class="my-n3">
                      <v-col cols="3" align-self="center" class="pr-0">
                        <v-label>Time Taken</v-label>
                      </v-col>
                      <v-col cols="4">
                        <decimal
                          :disabled="!model.completedDate || disabled"
                          :precision="2"
                          :pad="false"
                          v-model="model.hoursWorked"
                          placeholder="e.g. 1.5"
                          v-validate="'required'"
                          name="time taken"
                          :error-messages="errors.first('time taken')"
                          required
                          maxlength="6"
                        >
                          <template v-slot:append
                            >hrs</template
                          >
                        </decimal>
                      </v-col>
                    </v-row>
                    <v-row class="my-n3">
                      <v-col cols="3" align-self="center" class="pr-0">
                        <v-label>Category</v-label>
                      </v-col>
                      <v-col cols="9">
                        <v-select :items="categories" v-model="model.categoryId" item-value="id" item-text="name" v-validate="'required'" :error-messages="errors.first('category')" name="category" required :disabled="disabled"> </v-select>
                      </v-col>
                    </v-row>
                    <v-row class="my-n3">
                      <v-col cols="3" align-self="center" class="pr-0">
                        <v-label>Subcategory</v-label>
                      </v-col>
                      <v-col cols="9">
                        <v-select :items="subCategories" v-model="model.subCategoryId" item-value="id" item-text="name" :error-messages="errors.first('subcategory')" name="subcategory" :disabled="disabled"> </v-select>
                      </v-col>
                    </v-row>
                    <v-flex xs12>
                      <v-textarea v-model="model.notes" label="Repair Description" v-validate="'required'" name="repair description" :error-messages="errors.first('repair description')" required :disabled="disabled"></v-textarea>
                    </v-flex>
                    <v-flex xs12>
                      <v-text-field
                        v-model="model.completedBy"
                        placeholder="by who? (e.g. 060 - Kris Shepherd)"
                        v-validate="'required'"
                        name="completed by"
                        :error-messages="errors.first('completed by')"
                        required
                        maxlength="150"
                        :disabled="disabled"
                      >
                        <template v-slot:prepend
                          >Completed&nbsp;by</template
                        >
                      </v-text-field>
                    </v-flex>
                  </v-container>
                  <v-flex xs12 v-if="shouldOnroad()">
                    <v-checkbox v-model="model.onRoad" class="completed-date-input" :disabled="disabled">
                      <template v-slot:label
                        >On-road this vehicle</template
                      >
                    </v-checkbox>
                  </v-flex>
                  <v-flex xs12 v-if="model.onRoad && shouldOnroad()">
                    <date v-model="model.onRoadDate" v-validate="'required'" required name="on-road date" :error-messages="errors.first('on-road')" :disabled="disabled">
                      <template v-slot:prepend
                        >On&#x2011;road&nbsp;on</template
                      >
                    </date>
                  </v-flex>
                  <v-flex xs12>
                    <v-alert dense border="left" type="warning">
                      Reminder: Update Warranty information if required
                    </v-alert>
                  </v-flex>
                  <v-flex xs12>
                    <v-checkbox v-model="showWarranty" @change="$validator.validate()" :disabled="disabled">
                      <template v-slot:label
                        >Add Warranty</template
                      >
                    </v-checkbox>
                  </v-flex>
                  <v-flex xs12 v-if="showWarranty">
                    <v-container>
                      <v-layout wrap>
                        <v-flex xs12>
                          <v-text-field
                            v-model="warranty.description"
                            label="What is the description?"
                            placeholder="e.g. Fuel pump, 12 month warranty"
                            v-validate="'required'"
                            :error-messages="errors.first('description')"
                            name="description"
                            maxlength="150"
                            :required="showWarranty"
                            :disabled="disabled"
                          ></v-text-field>
                        </v-flex>
                        <v-flex xs12>
                          <v-text-field v-model="warranty.ednNumber" label="EDN Number" name="ednnumber" maxlength="500" disabled></v-text-field>
                        </v-flex>
                        <v-flex xs12>
                          <v-text-field :value="formattedStartDate" label="When does the warranty start?" disabled name="startDate"></v-text-field>
                        </v-flex>
                        <v-flex xs6>
                          <v-text-field
                            v-model="warranty.length"
                            label="The warranty length"
                            v-validate="'required|min_value:1'"
                            :error-messages="errors.collect('length')"
                            name="length"
                            type="number"
                            min="1"
                            :required="showWarranty"
                            :disabled="disabled"
                          ></v-text-field>
                        </v-flex>
                        <v-flex xs6>
                          <v-select
                            label="months / years"
                            item-value="key"
                            item-text="value"
                            :items="warrantyIntervalTypes"
                            v-model="warranty.warrantyInterval"
                            v-validate="'required'"
                            :error-messages="errors.collect('warranty interval')"
                            name="warranty interval"
                            :required="showWarranty"
                            :disabled="disabled"
                          ></v-select>
                        </v-flex>
                      </v-layout>
                    </v-container>
                  </v-flex>
                  <v-flex xs12 v-if="userIsFleetManager">
                    <v-checkbox v-model="disableMeterValidation" @change="$validator.validate()" :disabled="disabled">
                      <template v-slot:label
                        >Disable Meter Validation</template
                      >
                    </v-checkbox>
                  </v-flex>
                </v-flex>
              </v-layout>
              <v-card-actions>
                <v-spacer></v-spacer>
                <v-btn @click="close">Cancel</v-btn>
                <v-btn color="red white--text" v-if="initiatedFrom === 'for-review'" @click="reject">Reject</v-btn>
                <v-btn color="primary" :disabled="disableSubmit || disabled" @click="submit">Complete</v-btn>
              </v-card-actions>
            </v-col>
            <v-col v-if="showAuditLog" cols="6">
              <defect-audit-log :defect-id="defect.id" :defect-number="defect.lineNumber" :work-sessions="defect.workSessions" :assignees="defect.assignees" :historical-accepted-jobs="defect.historicalAcceptedJobs || []"></defect-audit-log>
            </v-col>
          </v-row>
        </v-card>
      </v-wait>
    </v-dialog>
    <v-dialog persistent v-model="showRejectDefect" max-width="500px">
      <reject-defect-form :defect="defect" ref="rejectdefectform" @reject="submitRejectedDefect($event.id, $event.model)">
        <template v-slot:header>
          <v-btn @click="showRejectDefect = false" icon text>
            <v-icon>close</v-icon>
          </v-btn>
        </template>
        <template v-slot:actions>
          <v-btn @click="showRejectDefect = false">Cancel</v-btn>
        </template>
      </reject-defect-form>
    </v-dialog>
    <photo-modal ref="photoModal" :photo-url="photoModalData.photoUrl" :defect-id="photoModalData.defectId" @photo-deleted="handlePhotoDeleted" />
  </div>
</template>
<script>
import { Event } from '@/plugins/bus';
import DefectsClient from '@/components/DefectNotices/DefectNoticesClient.vue';
import { VclList } from 'vue-content-loading';
import moment from 'moment';
import Decimal from '@/components/Decimal.vue';
import Date from '@/components/Date.vue';
import { DateFormat } from '@/plugins/date-filter';
import MetricToleranceCheck from '@/components/MetricToleranceCheck';
import CustomWarrantyClient from '@/components/Units/Details/CustomWarranty/CustomWarrantyClient.vue';
import { WarrantyIntervalType } from '@/models/enums';
import RejectDefectForm from '@/components/DefectNotices/RejectDefectForm.vue';
import UnitsClient from '@/components/Units/UnitsClient.vue';
import PhotoModal from '@/components/PhotoModal.vue';
import DefectAuditLog from '@/components/DefectNotices/DefectAuditLog.vue';

export default {
  mixins: [DefectsClient, MetricToleranceCheck, CustomWarrantyClient, UnitsClient],
  components: {
    RejectDefectForm,
    VclList,
    Decimal,
    Date,
    PhotoModal,
    DefectAuditLog
  },
  data() {
    return {
      defect: null,
      show: null,
      showRejectDefect: null,
      showAuditLog: false,
      disabled: false,
      model: {},
      warranty: {},
      showWarranty: false,
      today: moment({ hour: 23, minute: 59, seconds: 59, milliseconds: 999 }).format(DateFormat.Canonical),
      mindate: null,
      intervalTypes: ['Hours', 'Kilometres'],
      warrantyIntervalTypes: [
        {
          key: WarrantyIntervalType.Months,
          value: 'Months'
        },
        {
          key: WarrantyIntervalType.Years,
          value: 'Years'
        }
      ],
      disableMeterValidation: false,
      categories: [],
      subCategories: [],
      photoModalData: {
        photoUrl: '',
        defectId: null
      }
    };
  },
  computed: {
    defectId() {
      return this.$route.query && this.$route.query.defectId;
    },
    initiatedFrom() {
      return (this.$route.query && this.$route.query.initiatedFrom) || 'unknown';
    },
    disableSubmit() {
      return Object.keys(this.fields).some(key => this.fields[key].validated && this.fields[key].invalid);
    },
    isHoursIntervalType() {
      return this.model.intervalType === 'Hours';
    },
    formattedStartDate() {
      return this.model.completedDate ? moment(this.model.completedDate).format(DateFormat.DefaultDisplay) : '';
    }
  },
  methods: {
    submit(event) {
      this.$validator.validateAll().then(this.complete);
    },
    reValidate(formInput, modelField) {
      this.$validator.validate(formInput, modelField);
    },
    reject() {
      setTimeout(() => this.$refs.rejectdefectform.init(true), 10);
      this.show = false;
      this.showRejectDefect = true;
      this.$router.back();
    },
    async submitRejectedDefect(id, model) {
      const result = await this.rejectDefect(id, model);
      if (result && result.status >= 200 && result.status <= 299) {
        this.showRejectDefect = false;
      }
    },
    complete(valid) {
      if (!valid) {
        return;
      }

      var done = () => {
        this.storeDefectCompletionData(this.defect.unitId, this.model.completedDate, {
          completedBy: this.model.completedBy,
          completionInterval: this.model.completionInterval,
          intervalType: this.model.intervalType,
          completedDate: this.model.completedDate
        });
        this.completeDefect(this.defectId, this.model, this.initiatedFrom);
        if (this.showWarranty) {
          this.addCustomWarranty(this.$uuid.v1(), this.warranty);
        }
      };

      var confirm = confirmed => {
        this.$confirmCancel.$off('confirm-cancel', confirm);
        if (!confirmed) {
          return;
        }
        done();
      };

      if (!this.model.onRoad && this.shouldOnroad()) {
        this.$confirmCancel.show(`Vehicle is Off Road`, `You are completing the final Urgent/MOR defect for this EDN but have not chosen to place the vehicle back on-road. Are you sure you want to complete without on-roading?`, 'Complete');
        this.$confirmCancel.$on('confirm-cancel', confirm);
      } else {
        done();
      }
    },
    close() {
      const isClear = !this.defect;
      this.defect = null;
      this.showAuditLog = false;

      if (!isClear) {
        this.$router.back();
      }
    },
    async resetModel() {
      this.disableMeterValidation = false;
      if (!this.defect) {
        return;
      }

      this.categories = await this.getUnitDefectCategories(this.defect.unitId);

      let cachedData = this.getDefectCompletionData(this.defect.unitId, this.defect.scheduledDate);

      this.model =
        this.defect.status === 'CompletedPendingReview'
          ? {
              defectId: this.defect.id,
              intervalType: this.defect.completionHours ? 'Hours' : 'Kilometres',
              completionInterval: this.defect.completionHours ? this.defect.completionHours : this.defect.completionKilometers,
              completedDate: cachedData.completedDate,
              completedBy: this.defect.completedBy,
              notes: this.defect.notes,
              hoursWorked: this.defect.hoursWorked,
              categoryId: this.defect.categoryId,
              subCategoryId: this.defect.subCategoryId,
              sourceSystem: this.defect.sourceSystem,
              onRoad: false,
              onRoadDate: null
            }
          : {
              defectId: this.defect.id,
              intervalType: cachedData.intervalType,
              completionInterval: cachedData.completionInterval,
              completedDate: cachedData.completedDate,
              completedBy: cachedData.completedBy,
              categoryId: this.defect.categoryId,
              subCategoryId: this.defect.subCategoryId,
              sourceSystem: this.defect.sourceSystem,
              notes: ''
            };

      this.mindate = moment(this.defect.receivedDate).format(DateFormat.Canonical);
      this.warranty = {
        defectId: this.defect.id,
        ednNumber: this.defect.ednNumber,
        unitId: this.defect.unitId,
        startDate: this.model.completedDate
      };
      this.showWarranty = false;
      this.disabled = this.defect.status !== 'Scheduled' && this.defect.status !== 'CompletedPendingReview';

      await this.$validator.reset();
    },
    shouldOnroad() {
      return this.defect.isVehicleOffRoad && this.defect.rating && this.defect.rating.requiresOffRoad && this.defect.totalOffroadableOpenDefects == 1;
    },
    openPhotoModal(photoUrl, defectId) {
      this.photoModalData.photoUrl = photoUrl;
      this.photoModalData.defectId = defectId;
      this.$refs.photoModal.showModal();
    },
    handlePhotoDeleted() {
      this.defect.photoBlobUri = '';
      this.photoModalData.photoUrl = '';
      this.$refs.photoModal.close();
    }
  },
  watch: {
    async show(val) {
      if (val) {
        this.defect = await this.getDefect(this.defectId);
        await this.resetModel();
      }
    },
    $route: {
      handler(val) {
        this.show = !!this.defectId;
      },
      deep: true
    },
    'model.categoryId': {
      async handler(newCategoryId) {
        this.subCategories = this.getUnitDefectSubCategories(newCategoryId);
      }
    }
  },
  async created() {
    this.show = !!this.defectId;
    this.$bus.$on(Event.DefectCompleted, this.close);

    if (this.show) {
      this.defect = await this.getDefect(this.defectId);
      await this.resetModel();
    }
    const isInRangeDefectHrs = async (value, params) => {
      if (this.disableMeterValidation) {
        return true;
      }
      return await this.checkMetricIsInRange(this.defect.unitId, 'Hours', value, params[0]);
    };
    const isInRangeDefectKms = async (value, params) => {
      if (this.disableMeterValidation) {
        return true;
      }
      return await this.checkMetricIsInRange(this.defect.unitId, 'Kilometres', value, params[0]);
    };
    this.$validator.extend('completed-in-range-defect-hrs', {
      validate: isInRangeDefectHrs,
      getMessage: (field, params, data) => data.message
    });
    this.$validator.extend('completed-in-range-defect-kms', {
      validate: isInRangeDefectKms,
      getMessage: (field, params, data) => data.message
    });
  },
  destroyed() {
    this.$bus.$off(Event.DefectCompleted, this.close);
    this.showAuditLog = false;
  }
};
</script>
<style>
.completed-date-input .v-text-field {
  padding-top: 0px;
  margin-top: 0px;
}
</style>
<style scoped>
    ::v-deep .v-toolbar__title {
        overflow: visible;
        text-overflow: unset;
        white-space: normal;
    }
</style>
